import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageTop from '../../../core/components/PageTop';
import MFRRestrictionsListTable from './table/MFRRestrictionsListTable';
import FlyOutMenu from '../../../core/components/FlyOutMenu';
import { useSelectRow } from '../../../core/table/hooks/useSelectRow';
import FormEdit from '../../../core/components/form/FormEdit';
import ValidInputText from '../../../core/components/validation/ValidInputText';
import { useEditRestrictionsModel } from './edit/useEditRestrictionsModel';
import FormSubmit from '../../../core/components/form/FormSubmit';
import Button from '../../../core/components/Button';
import Dialog from '../../../core/components/Dialog';
import { useMFRRestrictionsListTableInit } from './table/useMFRRestrictionsListTableInit';
import ValidInputMultiSelect from '../../../core/components/validation/ValidInputMultiSelect';
import SelectRowRenderer from '../../../core/table/renderers/SelectRowRenderer';
import { useMFRRestrictionsListData } from './table/useMFRRestrictionsListData';
import { ActionsFeature } from '../../../core/table/table.types';
import SelectAllToggleRenderer from '../../../core/table/renderers/SelectAllToggleRenderer';
import { useMFRRestrictionsListTableState } from './table/useMFRRestrictionsListTableState';

const dialog = {
    title: 'Edit Multiple Restrictions',
    message: 'Confirm you wish to edit multiple restrictions?',
};

const PageAdminMFRRestrictionsList = (): JSX.Element => {
    const tableRef = useRef<HTMLDivElement>(null);
    const [showModal, setShowModal] = useState<boolean>(false);
    const { selectedRows, setSelectedRows } = useSelectRow();
    const { model, setPropText, submit } = useEditRestrictionsModel(selectedRows);
    const { refetch: _refetch } = useMFRRestrictionsListTableInit();
    const { paging } = useMFRRestrictionsListTableState();

    useEffect(() => {
        /* Anytime paging changes, clear all selected rows */
        setSelectedRows([]);
        /* eslint-disable react-hooks/exhaustive-deps */
    }, [paging]);
    /**
     * This hook needed to be hoisted up to this level, so that the row selection
     * feature would have access to the quantity of rows at any given moment.
     */
    const { items } = useMFRRestrictionsListData();

    const selectedRowsLength = useMemo(() => Object.keys(selectedRows).length, [selectedRows]);

    const handleFacilitySelect = (val: (string | number)[]): void => {
        setPropText('CeRestrictTypes', val);
    };

    const handleExemptStatesSelect = (val: (string | number)[]): void => {
        setPropText('ExemptStates', val);
    };

    const refetch = useCallback((): void => {
        if (_refetch) {
            _refetch();
        }
        setSelectedRows([]);
    }, [_refetch, setSelectedRows]);

    const dialogActions = useMemo(
        () => [
            {
                label: 'OK',
                onClick: (): void => {
                    setShowModal(false);
                    submit(refetch);
                },
            },
            {
                label: 'CANCEL',
                onClick: (): void => setShowModal(false),
                cssName: 'btn-cancel',
            },
        ],
        [submit, setShowModal, refetch],
    );

    const submitForm = useCallback((): void => {
        if (selectedRowsLength > 1) {
            setShowModal(true);
        } else {
            submit(refetch);
        }
    }, [selectedRowsLength, submit, refetch]);

    const menuIsOpen = useMemo(() => selectedRowsLength > 0, [selectedRowsLength]);

    const actionFeatures: ActionsFeature[] = useMemo(() => {
        return [
            {
                columnTitle: 'Select',
                cellRenderer: ({ row }) => (
                    <SelectRowRenderer
                        row={row}
                        onClick={setSelectedRows}
                        selected={selectedRows[row.Id] ?? false}
                    />
                ),
                columnFilterRenderer: () => (
                    <SelectAllToggleRenderer
                        selected={selectedRowsLength === items.length}
                        onClick={(): void => setSelectedRows(selectedRowsLength === items.length ? [] : items, true)}
                    />
                ),
            },
        ];
    }, [items, selectedRows, selectedRowsLength, setSelectedRows]);

    const generateStatesExemptPlaceholder = useCallback((opts: number) => `${opts} states exempt`, []);

    return (
        <>
            <PageTop
                title="Manufacturer Restrictions List"
                icon="sprite-warn"
            />
            <div
                className="mfr-restrictions-feature mfr-restrictions-list"
                ref={tableRef}
            >
                <MFRRestrictionsListTable
                    onClick={(row): void => setSelectedRows([row])}
                    selectedRows={selectedRows}
                    actions={actionFeatures}
                    rows={items}
                />
            </div>
            <Dialog
                open={showModal}
                setOpen={setShowModal}
                title={dialog.title}
                message={dialog.message}
                actions={dialogActions}
            />
            <FlyOutMenu
                title="Edit Restriction(s)"
                isOpen={menuIsOpen}
                onClose={(): void => setSelectedRows([])}
                width={530}
                ignoreClickRef={tableRef}
            >
                <div className="mfr-fly-out-menu">
                    <p>
                        You have {selectedRowsLength} {selectedRowsLength > 1 ? 'rows' : 'row'} selected.
                    </p>
                    <FormEdit
                        formName="mfr-restrictions-edit"
                        cssName="mfr-restrictions-edit-form"
                    >
                        <ValidInputText
                            prop={model.ParentCo}
                            placeholder={model.ParentCo.lbl}
                            maxlen={50}
                            onChange={setPropText}
                        />
                        <ValidInputMultiSelect
                            id="mfr-restrictions-facility-types"
                            placeholder="Choose Facilities..."
                            prop={model.CeRestrictTypes}
                            onOptionSelect={handleFacilitySelect}
                            textInputWidth={350}
                        />
                        <ValidInputMultiSelect
                            id="mfr-restrictions-exempt-states"
                            placeholder="Choose States to Exempt..."
                            someOptionsSelectedPlaceholder={generateStatesExemptPlaceholder}
                            allOptionsSelectedPlaceholder="All states exempt"
                            prop={model.ExemptStates}
                            onOptionSelect={handleExemptStatesSelect}
                            textInputWidth={350}
                            showClearAllOption
                        />
                        <FormSubmit>
                            <Button onClick={submitForm}>{selectedRowsLength > 1 ? 'APPLY TO ALL' : 'SAVE'}</Button>
                            <span>
                                <Button
                                    cssName="btn-cancel"
                                    onClick={() => setSelectedRows([])}
                                >
                                    CANCEL
                                </Button>
                            </span>
                        </FormSubmit>
                    </FormEdit>
                </div>
            </FlyOutMenu>
        </>
    );
};

export default PageAdminMFRRestrictionsList;
